import { useParams, Link, Navigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionHeader, Kicker } from "@/components/Bits";
import { CtaRow, CheckAvailBtn } from "@/components/CTAButtons";
import { ParallaxImage } from "@/components/ParallaxImage";
import { ROOMS, HOTEL, IMG } from "@/data/site";
import { motion } from "framer-motion";
import { maskChild, staggerParent, scaleIn } from "@/lib/motion";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";

export default function RoomDetail() {
  const { slug } = useParams();
  const idx = ROOMS.findIndex((r) => r.slug === slug);
  if (idx === -1) return <Navigate to="/rooms" replace />;
  const room = ROOMS[idx];
  const next = ROOMS[(idx + 1) % ROOMS.length];

  return (
    <>
      <Seo
        title={`${room.name} in Ottawa, KS`}
        description={`${room.desc} Book the ${room.name} at ${HOTEL.name} direct for the best rate.`}
        path={`/rooms/${room.slug}`}
        image={room.image}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Rooms", path: "/rooms" },
          { name: room.name, path: `/rooms/${room.slug}` },
        ]}
      />

      <section className="relative flex min-h-[80vh] items-end overflow-hidden bg-charcoal">
        <motion.div variants={scaleIn} initial="hidden" animate="show" className="absolute inset-0">
          <img src={room.image} alt={`${room.name} at ${HOTEL.name}`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/25" />
        </motion.div>
        <div className="relative mx-auto w-full max-w-[1400px] px-5 pb-16 pt-36 md:px-10 md:pb-20">
          <Link to="/rooms" data-testid="back-to-rooms" className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-wide text-white/75 transition-colors hover:text-brand-yellow">
            <ArrowLeft className="h-4 w-4" /> All rooms
          </Link>
          <p className="mt-6"><Kicker tone="yellow">{room.beds} · {room.sleeps}</Kicker></p>
          <motion.h1 variants={staggerParent} initial="hidden" animate="show" className="mt-3 font-display text-6xl uppercase leading-[0.85] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <span className="mask-reveal"><motion.span variants={maskChild} className="block">{room.name}</motion.span></span>
          </motion.h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeader kicker="The Room" title="Room details" sub={room.desc} />
            <Reveal i={3} className="mt-10"><CtaRow /></Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal className="border-2 border-charcoal bg-bone p-8">
              <p className="font-heading text-sm font-black uppercase tracking-wide text-brand-red">In this room</p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {room.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 font-body text-sm text-charcoal/90">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <ParallaxImage src={IMG.pool2} alt={`Enjoy the indoor pool during your stay in the ${room.name}`} height="h-[55vh]" className="border-x-0" />

      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="flex flex-col items-start justify-between gap-8 border-t border-charcoal/10 pt-14 md:flex-row md:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-fog">Up next</p>
            <Link to={`/rooms/${next.slug}`} data-testid="next-room" className="mt-2 inline-flex items-center gap-3 font-display text-4xl uppercase tracking-tight text-charcoal transition-colors hover:text-brand-red md:text-5xl">
              {next.name} <ArrowRight className="h-8 w-8" />
            </Link>
          </div>
          <CheckAvailBtn />
        </div>
      </section>
    </>
  );
}
